import { useMemo } from "react";
import { Modal } from "@/components/modal/Modal";
import { useNavigate, useSearch } from "@tanstack/react-router";
import {
  consumeAuthHandoff,
  getRedirect,
  clearRedirect,
} from "@/lib/storage-utils";
import { getPersonaLandingPath } from "@/lib/post-login-redirect";
import { pendingEmailVerificationRedirect } from "@/lib/auth";
import { fetchUserAttributes } from "@aws-amplify/auth";
import AuthFlow from "./AuthFlow";
import type { AuthView } from "./AuthFlow";

export function AuthModal() {
  const search = useSearch({ from: "__root__" });
  const navigate = useNavigate();

  const authParam = (search as { auth?: string }).auth as AuthView | undefined;
  const isOpen = !!authParam;

  const handoff = useMemo(
    () => (isOpen ? consumeAuthHandoff() : null),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [authParam],
  );

  const stripAuthParam = () => {
    navigate({
      to: ".",
      search: (prev) => {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { auth: _, ...rest } = prev;
        return rest;
      },
      replace: true,
      resetScroll: false,
    });
  };

  const handleClose = () => {
    clearRedirect();
    stripAuthParam();
  };

  const handleSuccess = async () => {
    // A stashed code minted for someone else must not be replayed here.
    const authedEmail = await fetchUserAttributes()
      .then((attrs) => attrs.email ?? null)
      .catch(() => null);

    const pendingVerification = pendingEmailVerificationRedirect(authedEmail);
    if (pendingVerification) {
      window.location.replace(pendingVerification);
      return;
    }

    const redirect = getRedirect();
    if (redirect) {
      clearRedirect();
      navigate({
        to: redirect.pathname,
        search: redirect.search,
        replace: true,
      });
      return;
    }

    // Artists and label reps always land on their workspace (/audiolab).
    const personaLanding = await getPersonaLandingPath();
    if (personaLanding) {
      navigate({ to: personaLanding, replace: true });
      return;
    }

    stripAuthParam();
  };

  if (!isOpen) return null;

  return (
    <Modal
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) handleClose();
      }}
      size="smd"
      hideCloseButton={false}
      containerClassName="overflow-y-auto"
      mobileFullScreen
    >
      <div className="py-4">
        <AuthFlow
          initialView={authParam}
          initialEmail={handoff?.email}
          initialAuthMethods={handoff?.authMethods}
          initialPhase={handoff?.initialPhase}
          onSuccess={handleSuccess}
        />
      </div>
    </Modal>
  );
}

export default AuthModal;
